import dayjs from "dayjs";

// Shared, front-end-only lookups for the punch-editor apply panel (the "Edit
// Attendance" reference design) used by BOTH Attendance Correction and Shift
// Correction. No backend endpoint exists yet, so shifts are a static catalogue;
// swap SHIFT_OPTIONS for a `useFetch(...)` of the department-shift API later and
// nothing else in the panel has to change.
export const SHIFT_OPTIONS = [
  { id: "standard", label: "Standard Shift", start: "09:30", end: "18:30" },
  { id: "morning", label: "Morning Shift", start: "06:00", end: "14:00" },
  { id: "general", label: "General Shift", start: "10:00", end: "19:00" },
  { id: "evening", label: "Evening Shift", start: "14:00", end: "22:00" },
  { id: "night", label: "Night Shift", start: "22:00", end: "06:00" },
];

export const shiftMeta = (shiftId) =>
  SHIFT_OPTIONS.find((s) => s.id === shiftId) || SHIFT_OPTIONS[0];

// Each punch row is a direction — matches the reference IN / OUT dropdown.
export const DIRECTION_OPTIONS = [
  { id: "IN", label: "IN" },
  { id: "OUT", label: "OUT" },
];

// A fresh, empty punch row. `rowId` is a stable client key for add/remove.
export const makePunchRow = (rowId, date = dayjs()) => ({
  rowId,
  date: date ? dayjs(date) : null,
  time: null,
  direction: "IN",
});

// "hh:mm A" for a dayjs value, else "" — the display form the correction tables
// already render (e.g. "09:00 AM").
export const formatPunchTime = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("HH:mm") : "";

// A punch row's full moment: its own date (the time picker's value carries an
// arbitrary one) at its time. Falls back to the time value's date when the
// row has no date.
export const punchAt = (entry) => {
  const time = dayjs(entry.time);
  const date =
    entry.date && dayjs(entry.date).isValid() ? dayjs(entry.date) : time;
  return date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
};

const hasDateAndTime = (entry) =>
  Boolean(
    entry &&
      entry.time &&
      dayjs(entry.time).isValid() &&
      entry.date &&
      dayjs(entry.date).isValid(),
  );

// Minutes since midnight for a shift time ("HH:mm", "HH:mm:ss" or "hh:mm A"),
// else null.
const shiftMinutes = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)?$/i);
  if (!match) return null;
  const meridiem = match[3] && match[3].toUpperCase();
  let hour = Number(match[1]);
  if (meridiem) hour = (hour % 12) + (meridiem === "PM" ? 12 : 0);
  return hour * 60 + Number(match[2]);
};

// A shift that ends at or before its start time runs past midnight. Unknown
// times (no shift, week off) are not overnight.
export const isOvernightShift = (start, end) => {
  const startMinutes = shiftMinutes(start);
  const endMinutes = shiftMinutes(end);
  return startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes;
};

// Overnight shifts: a punch after midnight happens the calendar day after the
// AR date, but a new row starts on the date of the row above it. When the row's
// `patch` would put it at or before that punch on the same day, it's moved to
// the next day (unless that passes `maxDate`) and flagged `autoRolledDate`;
// a row moved that way whose time is edited back to after that punch returns
// to its day. A date the user picks themselves clears the flag in the panel,
// so it is never second-guessed. Returns the patch to apply to the row.
export const withOvernightDate = (entries = [], rowId, patch, maxDate) => {
  const index = entries.findIndex((entry) => entry.rowId === rowId);
  if (index < 0) return patch;
  const row = { ...entries[index], ...patch };
  const previous = entries.slice(0, index).reverse().find(hasDateAndTime);
  if (!previous || !hasDateAndTime(row)) return patch;

  const previousAt = punchAt(previous);
  const previousDay = dayjs(previous.date).startOf("day");
  const fitsPreviousDay = punchAt({ ...row, date: previousDay }).isAfter(previousAt);

  if (dayjs(row.date).isSame(previousDay, "day") && !fitsPreviousDay) {
    const nextDay = previousDay.add(1, "day");
    if (maxDate && nextDay.isAfter(dayjs(maxDate), "day")) return patch;
    return { ...patch, date: nextDay, autoRolledDate: true };
  }
  if (
    row.autoRolledDate &&
    dayjs(row.date).isSame(previousDay.add(1, "day"), "day") &&
    fitsPreviousDay
  ) {
    return { ...patch, date: previousDay, autoRolledDate: false };
  }
  return patch;
};

// Collapse the edited punch rows down to the single first-IN / last-OUT pair the
// correction history tables show. Rows without a time are ignored. Compared
// by date and time, so an overnight shift's after-midnight punches sort last.
export const deriveInOut = (entries = []) => {
  const timed = entries
    .filter((e) => e.time && dayjs(e.time).isValid())
    .map((e) => ({ direction: e.direction, t: punchAt(e) }));
  const ins = timed.filter((e) => e.direction === "IN").map((e) => e.t);
  const outs = timed.filter((e) => e.direction === "OUT").map((e) => e.t);
  const earliest = (list) =>
    list.reduce((min, t) => (!min || t.isBefore(min) ? t : min), null);
  const latest = (list) =>
    list.reduce((max, t) => (!max || t.isAfter(max) ? t : max), null);
  return {
    corrected_in: ins.length ? earliest(ins).format("HH:mm") : "",
    corrected_out: outs.length ? latest(outs).format("HH:mm") : "",
  };
};
